// src/redux/slices/userSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// ─────────────────────────────────────────────
// 📌 Fetch all users (search + pagination)
// ─────────────────────────────────────────────
export const fetchUsers = createAsyncThunk(
  "users/fetchAll",
  async (
    { page = 1, limit = 10, search = "", role = "", isBlocked = "", verifiedBadge = "" } = {},
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams({
        page,
        limit,
        ...(search       && { search }),
        ...(role         && { role }),
        ...(isBlocked    && { isBlocked }),
        ...(verifiedBadge && { verifiedBadge }),
      });

      const res = await api.get(`/all-users?${params.toString()}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch users"
      );
    }
  }
);

// ─────────────────────────────────────────────
// 📌 Fetch all admins
// ─────────────────────────────────────────────
export const fetchAdmins = createAsyncThunk(
  "users/fetchAdmins",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/all-admins");
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

// ─────────────────────────────────────────────
// 📌 Get single user by ID
// ─────────────────────────────────────────────
export const fetchSingleUser = createAsyncThunk(
  "users/fetchSingle",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/user/${id}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

// ─────────────────────────────────────────────
// 📌 Update user
// ─────────────────────────────────────────────
export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/update-user/${id}`, userData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

// ─────────────────────────────────────────────
// 📌 Update admin
// ─────────────────────────────────────────────
export const updateAdmin = createAsyncThunk(
  "users/updateAdmin",
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/update-admin/${id}`, userData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

// ─────────────────────────────────────────────
// 📌 Delete user
// ─────────────────────────────────────────────
export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/delete-user/${id}`);
      return { ...data, id };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

// ─────────────────────────────────────────────
// 📌 Delete admin
// ─────────────────────────────────────────────
export const deleteAdmin = createAsyncThunk(
  "users/deleteAdmin",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/delete-admin/${id}`);
      return { ...data, id };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

// ─────────────────────────────────────────────
// 📌 Leaderboard
// ─────────────────────────────────────────────
export const fetchLeaderboard = createAsyncThunk(
  "users/fetchLeaderboard",
  async (
    { page = 1, limit = 10, search = "", type = "user" } = {},
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(
        `/reward/admin/leaderboard?page=${page}&limit=${limit}&search=${search}&type=${type}`
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch leaderboard"
      );
    }
  }
);

// ─────────────────────────────────────────────
// 📌 User points history
// ─────────────────────────────────────────────
export const getUserPointsHistory = createAsyncThunk(
  "users/getUserPointsHistory",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/user/points-transactions-history/${userId}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch points history"
      );
    }
  }
);

// ─────────────────────────────────────────────
// Initial State
// ─────────────────────────────────────────────
const initialState = {
  users: [],
  usersPagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  },

  admins: [],

  leaderboard: [],
  leaderboardPagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  },

  pointsHistory: {
    referralCode: "",
    points: 0,
    data: [],
  },

  selectedUser: null,
  loading: false,
  error: null,
  success: null,
};

// ─────────────────────────────────────────────
// Slice
// ─────────────────────────────────────────────
const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearUserMessages: (state) => {
      state.error = null;
      state.success = null;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // ── Fetch Users ───────────────────────────────────
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          // Backend returns: { success, data: { users, total, page, pages, limit } }
          const { users, total, page, pages, limit } = action.payload.data;
          state.users = users;
          state.usersPagination = {
            total:      total      ?? 0,
            page:       page       ?? 1,
            totalPages: pages      ?? 1,
            limit:      limit      ?? 10,
          };
        } else {
          state.error = action.payload.message || "Failed to fetch users";
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch users";
      })

      // ── Fetch Admins ──────────────────────────────────
      .addCase(fetchAdmins.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.admins = action.payload.data;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch admins";
      })

      // ── Fetch Single User ─────────────────────────────
      .addCase(fetchSingleUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSingleUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.selectedUser = action.payload.data;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(fetchSingleUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch user";
      })

      // ── Update User ───────────────────────────────────
      .addCase(updateUser.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.users = state.users.map((u) =>
            u._id === action.payload.data._id ? action.payload.data : u
          );
          // also sync selectedUser if open
          if (state.selectedUser?._id === action.payload.data._id) {
            state.selectedUser = action.payload.data;
          }
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload?.message || "Update failed";
      })

      // ── Update Admin ──────────────────────────────────
      .addCase(updateAdmin.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.admins = state.admins.map((a) =>
            a._id === action.payload.data._id ? action.payload.data : a
          );
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(updateAdmin.rejected, (state, action) => {
        state.error = action.payload?.message || "Update failed";
      })

      // ── Delete User ───────────────────────────────────
      .addCase(deleteUser.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.users = state.users.filter((u) => u._id !== action.payload.id);
          state.usersPagination.total = Math.max(0, state.usersPagination.total - 1);
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload?.message || "Delete failed";
      })

      // ── Delete Admin ──────────────────────────────────
      .addCase(deleteAdmin.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.admins = state.admins.filter((a) => a._id !== action.payload.id);
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(deleteAdmin.rejected, (state, action) => {
        state.error = action.payload?.message || "Delete failed";
      })

      // ── Leaderboard ───────────────────────────────────
      .addCase(fetchLeaderboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeaderboard.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.leaderboard = action.payload.data;
          state.leaderboardPagination = action.payload.pagination;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(fetchLeaderboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ── User Points History ───────────────────────────
      .addCase(getUserPointsHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserPointsHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.pointsHistory = action.payload;
      })
      .addCase(getUserPointsHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearUserMessages, clearSelectedUser } = userSlice.actions;
export default userSlice.reducer;